import { useEffect, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { Dumbbell, ChevronRight, Flame } from "lucide-react"

type WorkoutPill = {
  id: string;
  name: string;
  mode: "strong" | "heavy" | "simple";
  /** Quick timers only: the run has ENDED and is sitting unsaved. The pill
   *  used to vanish at exactly this moment — see readWorkoutPill. */
  needsSaving?: boolean;
}

// Recovery pill carries the wall-clock end time so the bar can render a
// live countdown without depending on RecoveryTab being mounted. Paused
// sessions store `cdEndTime: 0` and surface `cdRemaining` instead.
type RecoveryPill = {
  logType: string;
  name: string;
  mode: "countdown" | "stopwatch";
  running: boolean;
  cdEndTime: number;
  cdRemaining: number;
  swAccum: number;
  swStart: number;
}

// Must match the keys and event names used inside
// components/workout/MyWorkouts.tsx and components/RecoveryTab.tsx. Do not
// rename in one place without the others — the sticky bar and the tabs it
// resumes share this contract.
const KEY = "trybeup_active_workout"
const SIMPLE_DRAFT_KEY = "trybeup_simple_workout_draft"
const RECOVERY_DRAFT_KEY = "trybeup_recovery_timer_draft"
const CHANGE_EVENT = "trybeup:active-workout-changed"
const SWITCH_TAB_EVENT = "trybeup:switch-tab"
const SESSION_VISIBLE_EVENT = "trybeup:workout-session-visible"
// Asks MyWorkouts to re-read the active workout from storage and OPEN it.
// Switching tabs is not enough: when the user is already on the Workout tab
// (the common case — that's where the pill is most visible) a tab switch is a
// no-op, and MyWorkouts only read the marker at mount. The pill did nothing.
const RESUME_ACTIVE_WORKOUT_EVENT = "trybeup:resume-active-workout"

// Keys must match the `type` field on each entry of RECOVERY_TYPES in
// RecoveryTab.tsx — `cold_exposure`, not `cold`. A previous version of
// this map used `cold` and silently fell back to the generic "Recovery"
// label for every cold-exposure session. Keep these aligned with the
// RecoveryType union when adding new recovery types.
const RECOVERY_LABELS: Record<string, string> = {
  breathwork: "Breathwork",
  cold_exposure: "Cold Exposure",
  sauna: "Sauna",
  earthing: "Earthing",
  sunlight: "Sunlight",
  other: "Recovery",
}

/** Exported for tests: this decision hid a finished, unsaved workout from the
 *  user entirely, so it is worth pinning directly rather than through the DOM. */
export function readWorkoutPill(): WorkoutPill | null {
  // Strength sessions (Strong / Heavy) write here. We deliberately ignore
  // any stray mode:"simple" entry — the Simple timer is discovered via its
  // own draft key below, and mode:"simple" in this slot would trick
  // MyWorkouts into rendering the strength WorkoutSession.
  try {
    const raw = localStorage.getItem(KEY)
    if (raw) {
      const parsed = JSON.parse(raw)
      if (
        parsed &&
        typeof parsed.id === "string" &&
        typeof parsed.name === "string" &&
        (parsed.mode === "strong" || parsed.mode === "heavy")
      ) {
        return { id: parsed.id, name: parsed.name, mode: parsed.mode }
      }
    }
  } catch {}

  // Simple timer workouts keep their state here.
  //
  // The condition used to be `phase === "running" && endTime > Date.now()`,
  // which hid the pill THE MOMENT THE TIMER FINISHED — precisely when the
  // workout is done, unsaved, and most needs a route back to it. A 30-minute
  // run that ended while the user was on another tab became unreachable: the
  // pill was gone, and the page only re-reads the draft at mount, so the only
  // way back in was to force-quit the app (founder report 2026-08-14: "could
  // save my 30 min run in Quick — had to hard reset").
  //
  // The pill now shows for ANY unsaved draft. `handleLog` and
  // `handleCancelWorkout` both clear the draft, so its mere existence means
  // there is work the user hasn't dealt with yet.
  try {
    const raw = localStorage.getItem(SIMPLE_DRAFT_KEY)
    if (raw) {
      const d = JSON.parse(raw)
      const isDraft =
        d && (d.phase === "running" || d.phase === "done") && typeof d.endTime === "number"
      // Same 24h staleness rule loadSimpleDraft applies, so the two agree on
      // what still counts as a live draft.
      const fresh =
        isDraft &&
        typeof d.workoutStartedAt === "number" &&
        Date.now() - d.workoutStartedAt <= 24 * 60 * 60 * 1000
      if (fresh) {
        const stillCounting = d.phase === "running" && d.endTime > Date.now()
        const name =
          d.selectedActivity === "custom"
            ? (d.customActivity || "Workout")
            : (d.selectedActivity || "Workout")
        return { id: "simple", name, mode: "simple", needsSaving: !stillCounting }
      }
    }
  } catch {}

  return null
}

function readRecoveryPill(): RecoveryPill | null {
  // Recovery sessions (Breathwork / Cold / Sauna / Earthing / Sunlight / Other)
  // write the full timer draft here. We surface the pill whenever a
  // session is active — running OR paused with progress — so the user
  // can always jump back to it from any tab.
  try {
    const raw = localStorage.getItem(RECOVERY_DRAFT_KEY)
    if (!raw) return null
    const d = JSON.parse(raw)
    if (!d || typeof d.logType !== "string") return null
    if (d.timerMode !== "countdown" && d.timerMode !== "stopwatch") return null

    // Auto-expire stale drafts (older than 24h with no running flag).
    if (Date.now() - (d.savedAt ?? 0) > 24 * 60 * 60 * 1000) return null

    const name = RECOVERY_LABELS[d.logType] || "Recovery"
    const running = d.running === true

    if (d.timerMode === "countdown") {
      const cdEndTime = typeof d.cdEndTime === "number" ? d.cdEndTime : 0
      const cdRemaining = typeof d.cdRemaining === "number" ? d.cdRemaining : 0
      // Surface the pill if the timer is running, OR paused with time
      // remaining (so the user can resume), OR was running and just
      // expired (cdEndTime in the past while running stayed true). In
      // all cases the pill gives them a way back into the dialog.
      const liveRemaining = cdEndTime > 0 ? Math.max(0, Math.ceil((cdEndTime - Date.now()) / 1000)) : cdRemaining
      const hasProgress = running || liveRemaining > 0
      if (!hasProgress) return null
      return {
        logType: d.logType,
        name,
        mode: "countdown",
        running,
        cdEndTime,
        cdRemaining,
        swAccum: 0,
        swStart: 0,
      }
    }

    // stopwatch
    const swAccum = typeof d.swAccum === "number" ? d.swAccum : 0
    const swStart = typeof d.swStart === "number" ? d.swStart : 0
    if (!running && swAccum === 0) return null
    return {
      logType: d.logType,
      name,
      mode: "stopwatch",
      running,
      cdEndTime: 0,
      cdRemaining: 0,
      swAccum,
      swStart,
    }
  } catch {}
  return null
}

function formatMMSS(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds))
  const m = Math.floor(s / 60)
  const r = s % 60
  return `${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`
}

export function ActiveWorkoutSticky() {
  const [workout, setWorkout] = useState<WorkoutPill | null>(() => readWorkoutPill())
  const [recovery, setRecovery] = useState<RecoveryPill | null>(() => readRecoveryPill())
  const [sessionVisible, setSessionVisible] = useState(false)
  // Bumped every second while a recovery session is running so the live
  // countdown text re-renders. We compute remaining from `cdEndTime` on
  // each render, so this only needs to be a render-trigger.
  const [, setTick] = useState(0)
  const location = useLocation()
  const navigate = useNavigate()

  useEffect(() => {
    const reload = () => {
      setWorkout(readWorkoutPill())
      setRecovery(readRecoveryPill())
    }
    window.addEventListener("storage", reload)
    window.addEventListener(CHANGE_EVENT, reload)
    return () => {
      window.removeEventListener("storage", reload)
      window.removeEventListener(CHANGE_EVENT, reload)
    }
  }, [])

  useEffect(() => {
    const onVisibility = (e: Event) => {
      setSessionVisible(!!(e as CustomEvent).detail)
    }
    window.addEventListener(SESSION_VISIBLE_EVENT, onVisibility)
    return () => window.removeEventListener(SESSION_VISIBLE_EVENT, onVisibility)
  }, [])

  // Re-read the Quick draft while its timer is still counting, so the label
  // flips to "Finish saving your workout" the moment it ends.
  //
  // Nothing else fires at that moment — the draft in storage doesn't change
  // when wall-clock time passes it, and the CHANGE_EVENT only fires on a
  // write. Without this the pill would sit reading "Workout in progress" for a
  // run that finished, which is the same misreport in a new place.
  useEffect(() => {
    if (workout?.mode !== "simple" || workout.needsSaving) return
    const id = window.setInterval(() => setWorkout(readWorkoutPill()), 1000)
    return () => window.clearInterval(id)
  }, [workout?.mode, workout?.needsSaving])

  // Drive the live countdown text on the recovery pill. Runs only when a
  // recovery session is currently surfaced, so we don't pay the tick cost
  // when nothing's active.
  useEffect(() => {
    if (!recovery) return
    const id = window.setInterval(() => setTick((n) => n + 1), 1000)
    return () => window.clearInterval(id)
  }, [recovery])

  // Don't show on pages where switching tabs doesn't make sense (password
  // reset, landing, static content). The bar is meant to rescue users who
  // lose their way back to an in-progress session inside the main app.
  const skipPaths = ["/reset-password", "/landing", "/login", "/white-paper", "/75-hard", "/challenges"]
  const onSkippedRoute = skipPaths.some((p) => location.pathname.startsWith(p))

  if (onSkippedRoute || sessionVisible) return null
  if (!workout && !recovery) return null

  const onWorkoutClick = () => {
    if (!workout) return
    // Match the workout-tab's mode (Simple / Strong / Heavy) so we land
    // on the right view — not a different mode the user happened to
    // select afterwards.
    try {
      localStorage.setItem("trybeup_workout_mode", workout.mode)
    } catch {}
    window.dispatchEvent(
      new CustomEvent("trybeup:switch-workout-mode", { detail: workout.mode }),
    )
    const onMainApp = location.pathname === "/" || location.pathname === "/app"
    if (onMainApp) {
      window.dispatchEvent(new CustomEvent(SWITCH_TAB_EVENT, { detail: "workout" }))
    } else {
      navigate("/?tab=workout")
    }
    // Then actually OPEN the session. Getting to the right tab in the right
    // mode is not the same as resuming — and when the user was already on that
    // tab, the two dispatches above changed nothing they could see.
    //
    // Deferred a frame so a just-switched tab has mounted its listener; the
    // handler is idempotent, so firing it twice or with nothing to resume is
    // harmless.
    requestAnimationFrame(() => {
      window.dispatchEvent(new CustomEvent(RESUME_ACTIVE_WORKOUT_EVENT))
    })
  }

  const onRecoveryClick = () => {
    if (!recovery) return
    // Recovery timers live under Workout → Recovery sub-tab. Tell
    // MyWorkouts to flip to that sub-tab AND tell RecoveryTab to bring
    // the Log dialog back if it's currently minimized. The dialog
    // listener no-ops when nothing's there to show, so dispatching is
    // always safe.
    window.dispatchEvent(
      new CustomEvent("trybeup:switch-workout-subtab", { detail: "recovery" }),
    )
    window.dispatchEvent(new CustomEvent("trybeup:show-recovery-dialog"))
    const onMainApp = location.pathname === "/" || location.pathname === "/app"
    if (onMainApp) {
      window.dispatchEvent(new CustomEvent(SWITCH_TAB_EVENT, { detail: "workout" }))
    } else {
      navigate("/?tab=workout")
    }
  }

  // Compute the live recovery readout (countdown remaining or stopwatch
  // elapsed) for the pill text. Only runs if `recovery` is non-null.
  let recoveryReadout = ""
  if (recovery) {
    if (recovery.mode === "countdown") {
      const remaining = recovery.cdEndTime > 0
        ? Math.max(0, Math.ceil((recovery.cdEndTime - Date.now()) / 1000))
        : recovery.cdRemaining
      recoveryReadout = formatMMSS(remaining)
    } else {
      const elapsed = recovery.running && recovery.swStart > 0
        ? recovery.swAccum + Math.floor((Date.now() - recovery.swStart) / 1000)
        : recovery.swAccum
      recoveryReadout = formatMMSS(elapsed)
    }
  }

  // Stack pills above the bottom tab bar. Recovery sits on top of the
  // workout pill so the live countdown is the more glanceable surface.
  // Each pill is positioned independently so either can render alone.
  return (
    <div
      className="fixed left-1/2 -translate-x-1/2 z-[150] flex flex-col items-center gap-2"
      style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 5rem)" }}
    >
      {recovery && (
        <button
          onClick={onRecoveryClick}
          aria-label={`Resume ${recovery.name} session: ${recoveryReadout}`}
          className="flex items-center gap-2 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-2 shadow-lg animate-fade-in active:scale-95 transition-transform"
          data-testid="sticky-recovery-pill"
        >
          <span className="relative flex h-2 w-2" aria-hidden="true">
            {recovery.running && (
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
            )}
            <span className="relative inline-flex rounded-full h-2 w-2 bg-white" />
          </span>
          <Flame className="w-4 h-4" aria-hidden="true" />
          <span className="text-sm font-semibold truncate max-w-[140px]">
            {recovery.name}
          </span>
          <span className="text-sm font-mono tabular-nums">
            {recoveryReadout}
          </span>
          <ChevronRight className="w-4 h-4" aria-hidden="true" />
        </button>
      )}
      {workout && (
        <button
          onClick={onWorkoutClick}
          aria-label={
            workout.needsSaving
              ? `Finish saving your ${workout.name} workout`
              : `Resume active workout: ${workout.name}`
          }
          className="flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-4 py-2 shadow-lg animate-fade-in active:scale-95 transition-transform"
          data-testid="sticky-workout-pill"
        >
          {/* Indicator dot + ping use bg-primary-foreground so they
              contrast against the bg-primary pill in every theme.
              bg-white would render white-on-white under Modern (where
              --primary is warm print-white) — same family of bug as
              the surface-token sweep, applied here at the descendant
              level. */}
          <span className="relative flex h-2 w-2" aria-hidden="true">
            {/* The ping means "running". A finished-but-unsaved run is not
                running, and animating it there would misreport the state. */}
            {!workout.needsSaving && (
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary-foreground opacity-75" />
            )}
            <span className="relative inline-flex rounded-full h-2 w-2 bg-primary-foreground" />
          </span>
          <Dumbbell className="w-4 h-4" aria-hidden="true" />
          <span className="text-sm font-semibold truncate max-w-[180px]">
            {workout.needsSaving ? "Finish saving your workout" : "Workout in progress"}
          </span>
          <ChevronRight className="w-4 h-4" aria-hidden="true" />
        </button>
      )}
    </div>
  )
}
